import React, { useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, KeyRound, LogIn, UserPlus, ShieldCheck } from "lucide-react";
import PasswordInput from "@/components/PasswordInput";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const MODES = { LOGIN: "login", SIGNUP: "signup", MAGIC: "magic", FORGOT: "forgot" };

// Redirect-safety: only allow internal paths starting with a single "/" —
// never a protocol-relative URL ("//evil.com") or an absolute URL.
function sanitizeReturnTo(raw) {
  const v = String(raw || "");
  if (!v.startsWith("/") || v.startsWith("//") || v.includes("://")) return "/home";
  return v;
}

export default function Auth() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const returnTo = useMemo(() => sanitizeReturnTo(params.get("returnTo")), [params]);
  const [mode, setMode] = useState(MODES.LOGIN);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const post = async (path, body) => {
    const res = await fetch(`${API_BASE}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.detail || "Request failed.");
    return data;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === MODES.LOGIN) {
        await post("/api/auth/login", { email, password });
        toast.success("Signed in");
        window.location.assign(returnTo);
      } else if (mode === MODES.SIGNUP) {
        const data = await post("/api/auth/signup", { email, password });
        if (data.userId) {
          toast.success("Account created");
          window.location.assign(returnTo);
        } else {
          // existing-email response → guide to login
          setMode(MODES.LOGIN);
          toast.message("You may already have an account — try signing in or reset your password.");
        }
      } else if (mode === MODES.MAGIC) {
        await post("/api/auth/magic-link", { email, returnTo });
        setSent(true);
        toast.success("Magic-link sent. Check your inbox.");
      } else if (mode === MODES.FORGOT) {
        await post("/api/auth/password/reset/request", { email });
        setSent(true);
        toast.success("If that email is registered, a reset link is on the way.");
      }
    } catch (err) {
      toast.error(err.message || "Failed");
    } finally { setBusy(false); }
  };

  if (sent) {
    return (
      <div className="max-w-md mx-auto py-12 text-center" data-testid="auth-sent-screen">
        <Mail className="w-10 h-10 mx-auto mb-3" style={{ color: "var(--rs-amber)" }} />
        <h1 className="text-xl font-semibold mb-1">Check your email</h1>
        <p className="text-[var(--rs-text-3)] text-sm">{mode === MODES.FORGOT
          ? "If that email is registered, a password-reset link has been sent."
          : `A sign-in link has been sent to ${email}.`}</p>
        <button onClick={() => { setSent(false); setMode(MODES.LOGIN); }}
          data-testid="auth-back-to-login"
          className="mt-4 text-[12px] text-[var(--rs-amber)] hover:underline">
          Back to sign in
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto py-6" data-testid="auth-page">
      <h1 className="text-2xl font-semibold mb-1 inline-flex items-center gap-2">
        <ShieldCheck className="w-5 h-5" style={{ color: "var(--rs-amber)" }} />
        {mode === MODES.SIGNUP ? "Create your RigSentinel account"
          : mode === MODES.FORGOT ? "Reset your password"
          : "Sign in to RigSentinel"}
      </h1>
      <p className="text-[var(--rs-text-3)] text-sm mb-5">
        {mode === MODES.MAGIC ? "We'll email you a one-time sign-in link." : "Use your email and password."}
      </p>

      {/* Method tabs (hidden in forgot-password mode) */}
      {mode !== MODES.FORGOT && (
        <div className="grid grid-cols-2 rounded-md p-0.5 border mb-4"
          style={{ borderColor: "var(--rs-border)", background: "rgba(22,24,29,0.65)" }}
          data-testid="auth-method-tabs">
          <button type="button" onClick={() => setMode(MODES.LOGIN)}
            data-testid="auth-tab-password"
            className="h-9 rounded-[6px] text-[11px] font-semibold tracking-[0.12em] uppercase"
            style={(mode === MODES.LOGIN || mode === MODES.SIGNUP)
              ? { background: "rgba(255,179,0,0.18)", color: "var(--rs-amber)" }
              : { color: "var(--rs-text-3)" }}>
            Password
          </button>
          <button type="button" onClick={() => setMode(MODES.MAGIC)}
            data-testid="auth-tab-magic"
            className="h-9 rounded-[6px] text-[11px] font-semibold tracking-[0.12em] uppercase"
            style={mode === MODES.MAGIC
              ? { background: "rgba(255,179,0,0.18)", color: "var(--rs-amber)" }
              : { color: "var(--rs-text-3)" }}>
            Email me a link
          </button>
        </div>
      )}

      <form onSubmit={submit} className="space-y-3">
        <label className="block">
          <span className="text-[11px] uppercase tracking-wide text-[var(--rs-text-3)]">Email</span>
          <input type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            data-testid="auth-input-email"
            className="mt-1 w-full bg-[var(--rs-bg-2)] border border-[var(--rs-border)] rounded-md px-3 py-2 text-sm" />
        </label>

        {(mode === MODES.LOGIN || mode === MODES.SIGNUP) && (
          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-[var(--rs-text-3)]">Password</span>
            <PasswordInput
              autoComplete={mode === MODES.SIGNUP ? "new-password" : "current-password"}
              required minLength={mode === MODES.SIGNUP ? 10 : 1}
              value={password} onChange={(e) => setPassword(e.target.value)}
              testId="auth-input-password"
              className="mt-1" />
            {mode === MODES.SIGNUP && (
              <span className="text-[10px] text-[var(--rs-text-3)] mt-1 block">Min 10 characters.</span>
            )}
          </label>
        )}

        <button type="submit" disabled={busy}
          data-testid="auth-submit"
          className="w-full h-10 rounded-md font-semibold text-sm inline-flex items-center justify-center gap-2"
          style={{ background: "var(--rs-amber)", color: "var(--rs-bg)" }}>
          {mode === MODES.SIGNUP ? (<><UserPlus className="w-4 h-4" /> Sign up</>)
            : mode === MODES.MAGIC ? (<><Mail className="w-4 h-4" /> Send magic link</>)
            : mode === MODES.FORGOT ? (<><KeyRound className="w-4 h-4" /> Send reset link</>)
            : (<><LogIn className="w-4 h-4" /> Sign in</>)}
        </button>
      </form>

      {/* Secondary actions */}
      <div className="mt-4 text-[12px] text-[var(--rs-text-3)] flex flex-wrap gap-x-4 gap-y-1">
        {mode === MODES.LOGIN && (
          <>
            <button type="button" data-testid="auth-link-signup" onClick={() => setMode(MODES.SIGNUP)} className="hover:underline">Create account</button>
            <button type="button" data-testid="auth-link-forgot" onClick={() => setMode(MODES.FORGOT)} className="hover:underline">Forgot password?</button>
          </>
        )}
        {mode === MODES.SIGNUP && (
          <button type="button" data-testid="auth-link-back-login" onClick={() => setMode(MODES.LOGIN)} className="hover:underline">Already have an account? Sign in</button>
        )}
        {(mode === MODES.MAGIC || mode === MODES.FORGOT) && (
          <button type="button" data-testid="auth-link-use-password" onClick={() => setMode(MODES.LOGIN)} className="hover:underline">Use password instead</button>
        )}
      </div>
    </div>
  );
}
